import { Database } from '@/types/db';
import {
  useOrganization,
  useOrganizationList,
  useSession,
  useUser as useUserClerk
} from '@clerk/nextjs';
import { createBrowserClient } from '@supabase/ssr';
import { useMemo, useEffect, useRef } from 'react';
import posthog from 'posthog-js';

export function useSupaClient() {
  const { session, isLoaded, isSignedIn } = useSession();
  const { workspace } = useWorkspace();
  const sessionRef = useRef(session);

  // Keep ref updated with latest session
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const supaClient = useMemo(() => {
    if (!session || !isLoaded || !isSignedIn || !workspace) return null;
    return createBrowserClient<Database>(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY!,
      {
        async accessToken() {
          // Use ref to always get the latest session
          const token = await sessionRef.current?.getToken();
          return token ?? null;
        }
      }
    );
  }, [isLoaded, isSignedIn, workspace, !!session]); // Depend on session existence, not object reference

  return supaClient;
}

export function useUser() {
  const params = useUserClerk();

  const isInternalUser = !!params?.user?.publicMetadata?.internal;
  const isPaidUser = !!params?.user?.publicMetadata?.paid;
  return {
    ...params,
    isInternalUser,
    isPaidUser
  };
}

/**
 * Returns a workspace - which can represent a clerk org or a personal workspace
 */
export function useWorkspace() {
  const params = useOrganization();
  const { user, isLoaded } = useUserClerk();

  // If no user, just return the personal org
  const workspace = useMemo(() => {
    if (!isLoaded || !user) return null;

    return params?.organization
      ? {
          id: params.organization.id,
          name: params.organization.name,
          personal: false
        }
      : {
          id: user.id,
          name: 'Personal Workspace',
          personal: true
        };
  }, [params?.organization, user?.id, isLoaded]);

  return {
    ...params,
    workspace,
    isLoaded
  };
}

export function useWorkspaces() {
  const { user, isLoaded: userIsLoaded } = useUserClerk();

  // `useOrganizationList({ userMemberships })` has been observed to get stuck in
  // `isLoading: true` even when the user is clearly a member of organizations
  // (visible on `user.organizationMemberships`). We therefore source the list
  // from the User resource directly and only use `setActive` here to switch.
  const { setActive } = useOrganizationList();

  const organizations =
    user?.organizationMemberships?.map((m) => m.organization) ?? [];

  const workspaces = useMemo(() => {
    if (!setActive || !userIsLoaded) return [];

    return [
      ...organizations.map((org) => ({
        id: org.id,
        name: org.name,
        personal: false,
        setActive: () => setActive({ organization: org })
      })),
      {
        id: user?.id || '',
        name: 'Personal Workspace',
        personal: true,
        setActive: () => setActive({ organization: null })
      }
    ];
  }, [organizations, user?.id, setActive, userIsLoaded]);

  return {
    workspaces
  };
}

export function usePostHogIdentification() {
  const { user, isLoaded } = useUserClerk();

  useEffect(() => {
    if (isLoaded && user) {
      // Identify the user in PostHog with their Clerk user ID and properties
      posthog.identify(user.id, {
        email: user.emailAddresses?.[0]?.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: user.fullName,
        imageUrl: user.imageUrl,
        createdAt: user.createdAt,
        // Add any custom properties from Clerk metadata
        internal: user.publicMetadata?.internal || false,
        paid: user.publicMetadata?.paid || false,
        // Add organization info if available
        organizationId: user.organizationMemberships?.[0]?.organization?.id,
        organizationName: user.organizationMemberships?.[0]?.organization?.name
      });
    }
  }, [user, isLoaded]);
  return { user, isLoaded };
}
